import React from 'react';
import { ExternalLink, ArrowRight, ShieldCheck, Building2, Factory, Plane, Wheat, Gem, Landmark, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { siriusPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const ICONS = { Building2, Factory, Plane, Wheat, Gem, Landmark, TrendingUp };

/**
 * Featured platform section for Smartinvest subsidiary — showcases SIRIUS.
 * Institutional tokenized real-asset platform on Solana.
 */
const SIRIUSSection = () => {
  const { pick } = useLanguage();
  const navy = siriusPlatform.brandColor;
  const lightAccent = siriusPlatform.accentColor;

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #070d1a 0%, #0a1322 100%)',
        borderColor: `${lightAccent}40`
      }}
      data-testid="sirius-section"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 w-[700px] h-[700px] rounded-full blur-3xl opacity-30"
          style={{ background: `radial-gradient(circle, ${lightAccent}50 0%, transparent 70%)` }}
        />
        {/* Star map background */}
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(255,255,255,0.5) 1px, transparent 1px), radial-gradient(circle at 70% 40%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 35% 70%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.5) 1px, transparent 1px), radial-gradient(circle at 55% 15%, rgba(123,156,192,0.5) 1px, transparent 1px)',
          backgroundSize: '180px 180px, 240px 240px, 200px 200px, 220px 220px, 160px 160px'
        }} />
      </div>

      <div className="container-swg relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span
            className="inline-block px-5 py-2 text-[11px] tracking-[0.25em] uppercase font-medium rounded-full"
            style={{ color: lightAccent, border: `1px solid ${lightAccent}60`, background: `${navy}40` }}
            data-testid="sirius-eyebrow"
          >
            {pick(siriusPlatform.eyebrow)}
          </span>

          <div className="mt-8 flex items-center justify-center gap-6 flex-wrap">
            <img
              src={siriusPlatform.logo}
              alt="SIRIUS"
              className="h-20 w-20 object-contain drop-shadow-[0_0_30px_rgba(123,156,192,0.6)]"
              data-testid="sirius-logo"
            />
            <h2 className="text-5xl md:text-6xl text-white font-serif tracking-[0.2em]">
              SIRIUS
            </h2>
          </div>
          <p className="mt-6 text-2xl md:text-3xl text-white font-light tracking-wide">
            Real asset. <span style={{ color: lightAccent }}>Real value.</span> Real impact.
          </p>
          <p className="mt-3 text-[#9AACB8] text-sm italic">
            {pick(siriusPlatform.tagline)}
          </p>
        </motion.div>

        {/* Preview + brief */}
        <div className="grid lg:grid-cols-5 gap-10 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="block relative group overflow-hidden border shadow-[0_20px_60px_-20px_rgba(74,107,138,0.6)]"
              style={{ borderColor: `${lightAccent}50` }}
              data-testid="sirius-preview-link"
            >
              <img
                src={siriusPlatform.preview}
                alt="SIRIUS platform preview"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div
                className="absolute bottom-4 right-4 px-4 py-2 backdrop-blur-md text-xs font-medium flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity tracking-widest uppercase"
                style={{ background: `${navy}cc`, border: `1px solid ${lightAccent}`, color: lightAccent }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Live
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <p className="text-lg text-white leading-relaxed mb-5">{pick(siriusPlatform.shortBrief)}</p>
            <p className="text-[#9AACB8] leading-relaxed mb-8 text-sm">{pick(siriusPlatform.longBrief)}</p>

            {/* Standards badges */}
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#9AACB8] mb-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: lightAccent }} />
                Audited under
              </p>
              <div className="flex flex-wrap gap-2">
                {siriusPlatform.standards.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1.5 text-xs font-mono tracking-wider rounded"
                    style={{ color: lightAccent, border: `1px solid ${lightAccent}60`, background: `${navy}30` }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="inline-flex items-center gap-3 px-10 py-4 font-semibold text-sm tracking-[0.2em] uppercase transition-all duration-300 hover:scale-[1.03]"
              style={{
                background: navy,
                color: '#ffffff',
                border: `1px solid ${lightAccent}`,
                boxShadow: `0 8px 30px ${navy}80`
              }}
              data-testid="sirius-cta"
            >
              {pick(siriusPlatform.cta)}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        {/* 7 engines grid */}
        <div className="mb-14">
          <h3 className="text-center text-[#9AACB8] uppercase tracking-[0.3em] text-xs mb-8 font-mono">
            {pick({ en: 'Seven specialized engines', fr: 'Sept moteurs spécialisés' })}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {siriusPlatform.engines.map((engine, index) => {
              const Icon = ICONS[engine.icon] || Building2;
              return (
                <motion.div
                  key={engine.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="p-4 flex flex-col items-center text-center gap-2.5 rounded-lg backdrop-blur-sm"
                  style={{ background: `${navy}40`, border: `1px solid ${lightAccent}30` }}
                  data-testid={`sirius-engine-${engine.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `${lightAccent}20`, border: `1px solid ${lightAccent}50` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: lightAccent }} />
                  </div>
                  <span className="text-white text-xs font-medium leading-tight">{pick(engine.name)}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Features strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto"
          data-testid="sirius-features"
        >
          {siriusPlatform.features.map((feature) => (
            <div
              key={feature.en}
              className="p-5 rounded-lg text-center"
              style={{ background: `${navy}50`, border: `1px solid ${lightAccent}30` }}
            >
              <p className="text-white text-sm font-medium">{pick(feature)}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SIRIUSSection;
